import type { TaggedValue } from "./Blackboard";
import type { Blackboard } from "./Blackboard";
import type { EventBus } from "./EventBus";
import type { AudioSnapshot } from "./AudioManager";
import type { Interpreter, Beat } from "./Interpreter";

const MAX_SNAPSHOTS = 50;

export interface SpriteState {
  asset: string;
  pos: string;
  z: number;
  scale?: number;
}

export interface BaseState {
  asset: string;
  mode?: string | undefined;
  scroll?: string | undefined;
  speed?: number | undefined;
  opacity?: number | undefined;
  loop?: boolean | undefined;
}

export interface Snapshot {
  seq: number;
  taken_at: number;
  schema_version: 1;

  scene: string;
  beat: number;
  pending: Beat[];

  vars: Record<string, TaggedValue>;
  visits: Record<string, number>;
  usedChoices: string[];
  seenBeats: string[];

  bg: { asset: string } | null;
  base: BaseState | null;
  sprites: Record<string, SpriteState>;

  audio: AudioSnapshot;
}

export interface RollbackAudioAdapter {
  getSnapshot(): AudioSnapshot;
  restoreSnapshot(snap: AudioSnapshot): void;
}

export class Rollback {
  private _buffer: Snapshot[] = [];
  private _seq = 0;
  private _restoring = false;
  private _unsubs: Array<() => void> = [];

  private _currentBg: { asset: string } | null = null;
  private _currentBase: BaseState | null = null;
  private _currentSprites = new Map<string, SpriteState>();

  constructor(
    private _interpreter: Interpreter,
    private _bb: Blackboard,
    private _audio: RollbackAudioAdapter | null,
    private _bus: EventBus,
  ) {
    this._interpreter.onBeforePause = () => this.checkpoint();

    this._unsubs.push(
      _bus.on("bg.set", (d) => {
        this._currentBg = { asset: d.asset };
      }),
      _bus.on("base.set", (d) => {
        this._currentBase = {
          asset: d.asset, mode: d.mode, scroll: d.scroll,
          speed: d.speed, opacity: d.opacity, loop: d.loop,
        };
      }),
      _bus.on("base.clear", () => {
        this._currentBase = null;
      }),
      _bus.on("sprite.show", (d) => {
        const state: SpriteState = { asset: d.asset, pos: d.pos ?? "center", z: d.z ?? 0 };
        if (d.scale !== undefined) state.scale = d.scale;
        this._currentSprites.set(d.id, state);
      }),
      _bus.on("sprite.hide", (d) => {
        this._currentSprites.delete(d.id);
      }),
    );
  }

  setAudioAdapter(adapter: RollbackAudioAdapter | null): void {
    this._audio = adapter;
  }

  get depth(): number {
    return this._buffer.length;
  }

  canRewind(): boolean {
    return this._buffer.length > 1;
  }

  checkpoint(): void {
    if (this._restoring) return;

    const snap: Snapshot = {
      seq: this._seq++,
      taken_at: typeof performance !== "undefined" ? performance.now() : Date.now(),
      schema_version: 1,

      scene: this._interpreter.currentScene,
      beat: this._interpreter.currentBeat,
      pending: this._interpreter.getPending(),

      vars: this._bb.snapshot(),
      visits: this._interpreter.getVisitsMap(),
      usedChoices: this._interpreter.getUsedChoices(),
      seenBeats: this._interpreter.getSeenBeats(),

      bg: this._currentBg ? { ...this._currentBg } : null,
      base: this._currentBase ? { ...this._currentBase } : null,
      sprites: this._snapshotSprites(),

      audio: this._audio ? this._audio.getSnapshot() : { channels: {}, masterVolume: 1 },
    };

    if (this._buffer.length >= MAX_SNAPSHOTS) {
      this._buffer.shift();
    }
    this._buffer.push(snap);

    this._bus.emit("rollback.snapshot", { seq: snap.seq, depth: this._buffer.length });
  }

  rewind(): boolean {
    if (!this.canRewind()) return false;

    this._buffer.pop();
    const snap = this._buffer[this._buffer.length - 1];

    const ruleEngine = this._interpreter.ruleEngine;
    ruleEngine.pause();
    this._restoring = true;
    try {
      this._bb.restore(snap.vars);

      if (this._audio) {
        this._audio.restoreSnapshot(snap.audio);
      }

      this._reconcileStage(snap);

      this._interpreter.restoreState(
        snap.scene,
        snap.beat,
        snap.pending,
        snap.visits,
        snap.usedChoices,
        snap.seenBeats ?? [],
      );
    } finally {
      this._restoring = false;
      ruleEngine.resume();
    }

    this._bus.emit("rollback.done", { seq: snap.seq });
    return true;
  }

  getSnapshot(index: number): Snapshot | null {
    if (index < 0 || index >= this._buffer.length) return null;
    return this._buffer[index];
  }

  getLatestSnapshot(): Snapshot | null {
    if (this._buffer.length === 0) return null;
    return this._buffer[this._buffer.length - 1];
  }

  clear(): void {
    this._buffer = [];
    this._seq = 0;
    this._currentBg = null;
    this._currentBase = null;
    this._currentSprites.clear();
  }

  shutdown(): void {
    this._interpreter.onBeforePause = null;
    for (const unsub of this._unsubs) unsub();
    this._unsubs = [];
    this.clear();
  }

  private _snapshotSprites(): Record<string, SpriteState> {
    const out: Record<string, SpriteState> = {};
    for (const [id, state] of this._currentSprites) {
      out[id] = { ...state };
    }
    return out;
  }

  restoreFromSave(snap: Snapshot): void {
    const ruleEngine = this._interpreter.ruleEngine;
    ruleEngine.pause();
    this._restoring = true;
    try {
      this._bb.restore(snap.vars);
      if (this._audio) {
        this._audio.restoreSnapshot(snap.audio);
      }
      this._reconcileStage(snap);
    } finally {
      this._restoring = false;
      ruleEngine.resume();
    }
    this._buffer = [];
    this._seq = 0;
    this._interpreter.restoreState(
      snap.scene,
      snap.beat,
      snap.pending,
      snap.visits,
      snap.usedChoices,
      snap.seenBeats ?? [],
    );
  }

  private _reconcileStage(snap: Snapshot): void {
    const targetBg = snap.bg;
    if (targetBg && (!this._currentBg || this._currentBg.asset !== targetBg.asset)) {
      this._bus.emit("bg.set", { asset: targetBg.asset, transition: "dissolve", duration: 0.2 });
    }

    const targetBase = snap.base ?? null;
    if (!targetBase && this._currentBase) {
      this._bus.emit("base.clear");
    } else if (targetBase && (!this._currentBase || this._currentBase.asset !== targetBase.asset)) {
      this._bus.emit("base.set", {
        asset: targetBase.asset, mode: targetBase.mode, scroll: targetBase.scroll,
        speed: targetBase.speed, opacity: targetBase.opacity, loop: targetBase.loop,
      });
    }

    const targetSpriteIds = new Set(Object.keys(snap.sprites));
    for (const id of this._currentSprites.keys()) {
      if (!targetSpriteIds.has(id)) {
        this._bus.emit("sprite.hide", { id });
      }
    }
    for (const [id, state] of Object.entries(snap.sprites)) {
      const current = this._currentSprites.get(id);
      if (!current || current.asset !== state.asset || current.pos !== state.pos || current.scale !== state.scale) {
        this._bus.emit("sprite.show", {
          id,
          asset: state.asset,
          pos: state.pos,
          z: state.z,
          ...(state.scale !== undefined ? { scale: state.scale } : {}),
        });
      }
    }

    this._currentBg = targetBg ? { ...targetBg } : null;
    this._currentSprites.clear();
    for (const [id, state] of Object.entries(snap.sprites)) {
      this._currentSprites.set(id, { ...state });
    }
  }
}
